import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Sparkles, Loader2, ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";

export default function ProductDetail() {
  const { id } = useParams();
  const { addToCart } = useApp();
  const [product, setProduct] = useState(null);
  const [aiDesc, setAiDesc] = useState(null);
  const [genLoading, setGenLoading] = useState(false);

  useEffect(() => {
    api.get(`/products/${id}`).then(({ data }) => {
      setProduct(data);
      setAiDesc(data.ai_description || null);
    });
  }, [id]);

  const generateDesc = async () => {
    setGenLoading(true);
    try {
      const { data } = await api.post(`/products/${id}/ai-description`);
      setAiDesc(data.ai_description);
    } finally {
      setGenLoading(false);
    }
  };

  if (!product) return (
    <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-24 flex items-center gap-2 text-neutral-500">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading…
    </div>
  );

  return (
    <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-12">
      <Link to="/shop/all" className="inline-flex items-center gap-1 font-mono-brand text-xs uppercase tracking-[0.2em] text-neutral-500 hover:text-black mb-8" data-testid="back-link">
        <ArrowLeft className="w-3 h-3" /> Back to shop
      </Link>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16">
        <div className="md:col-span-7">
          <div className="aspect-[4/5] overflow-hidden bg-neutral-100">
            <img
              src={product.image + "?auto=format&fit=crop&w=1400&q=85"}
              alt={product.name}
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        <div className="md:col-span-5 md:pt-8">
          <div className="font-mono-brand text-xs uppercase tracking-[0.3em] text-[#FF3B30]" data-testid="detail-category">
            / {product.category}
          </div>
          <h1 className="font-display font-black text-4xl md:text-5xl leading-tight tracking-tighter mt-3" data-testid="detail-name">
            {product.name}
          </h1>
          <div className="font-mono-brand text-lg mt-4" data-testid="detail-price">${product.price.toFixed(2)}</div>

          <p className="text-neutral-600 leading-relaxed mt-8">{product.description}</p>

          {/* AI-generated flourish */}
          <div className="mt-10 border-l-2 border-[#FF3B30] pl-4">
            <div className="font-mono-brand text-[10px] uppercase tracking-[0.2em] text-[#FF3B30] flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> Nova.copywrite
            </div>
            {aiDesc ? (
              <p className="italic text-neutral-800 mt-2 leading-relaxed" data-testid="ai-description">{aiDesc}</p>
            ) : (
              <button
                onClick={generateDesc}
                disabled={genLoading}
                className="mt-2 font-mono-brand text-xs uppercase tracking-[0.2em] border-b border-black hover:border-[#FF3B30] hover:text-[#FF3B30] pb-0.5 flex items-center gap-2 disabled:opacity-60"
                data-testid="generate-ai-desc-btn"
              >
                {genLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                {genLoading ? "Generating…" : "Ask Nova to describe →"}
              </button>
            )}
          </div>

          <div className="mt-10 flex flex-col gap-3">
            <Button
              onClick={() => addToCart(product.id, 1)}
              className="rounded-none h-14 bg-black text-white hover:bg-[#FF3B30] font-mono-brand text-xs uppercase tracking-[0.2em]"
              data-testid="detail-add-cart-btn"
            >
              Add to bag — ${product.price.toFixed(2)}
            </Button>
          </div>

          <div className="mt-10 space-y-3 border-t border-black/10 pt-6">
            {product.tags?.length > 0 && (
              <div className="flex flex-wrap gap-2" data-testid="detail-tags">
                {product.tags.map((t) => (
                  <span key={t} className="font-mono-brand text-[10px] uppercase tracking-[0.2em] border border-black/10 px-2 py-1">{t}</span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
